/**
 * <coffee-quiz>: the "find your coffee" recommender. It routes on the product
 * metafields the catalogue already carries — custom.roast, custom.origin,
 * custom.process, custom.tasting_notes, custom.altitude, custom.producer —
 * by building a filtered collection URL out of the answers.
 *
 * This element renders nothing. coffee-quiz.liquid ships every question and
 * every answer as a real GET form with a real submit button, so a visitor
 * without JS gets a plain list of questions whose answers navigate straight to
 * the matching collection. All this file does is hide the steps it is not on
 * and intercept submit to advance instead of navigate. If the module 404s the
 * first step is still on screen and still answerable — the fallback is the
 * markup, not a second copy of it.
 *
 * Destinations are MERGED, not last-one-wins. The first answer that names a
 * collection sets the path, every answer contributes its filter.p.m.custom.*
 * params, and an answer pointing at a product short-circuits everything (a
 * merchant who points one answer at a single bag means that bag). A blank
 * destination contributes nothing at all, and when nothing was contributed the
 * section's default collection carries the result — buildDestination() cannot
 * return an empty href, so the result step can never dead-end.
 *
 * Accessibility notes, because they are the load-bearing part here:
 * - Every step change writes to a role="status" region AND moves focus to the
 *   new step heading (tabindex="-1"), so the announcement and the caret land
 *   in the same place instead of narrating a screen the user has not reached.
 * - The first step is deliberately NOT announced. connectedCallback calls
 *   show() with { announce: false } — the same flag <selling-plan-picker> uses
 *   in component-product.js — because a live region firing during page load is
 *   noise nobody asked for, and it competes with the page title.
 * - Progress markers carry aria-current="step" on the active one only; the
 *   others carry no aria-current at all rather than aria-current="false",
 *   which some screen readers still announce.
 * - Back / Start over are real buttons, revealed only once this file runs, so
 *   a failed module leaves no controls that look live and are not.
 *
 * Nothing is cached at module scope. The theme editor re-renders the whole
 * section on every edit and detaches every node this element queried, so all
 * lookups happen inside connectedCallback against `this`.
 */
import { publish } from './global.js';

/* Namespaced here rather than added to PUB_SUB_EVENTS: global.js is shared by
   every island on every template, and the quiz is the only thing that speaks
   this event. Cart and analytics can subscribe by name without global.js
   growing a constant for a section that most pages never render. */
const QUIZ_COMPLETED = 'quiz:completed';

/* Last-ditch values only. The real copy comes from the [data-quiz-strings]
   JSON block, and the real default collection from data-default-destination. */
const DEFAULT_STRINGS = {
  stepAnnouncement: 'Step [current] of [total]: [question]',
  resultAnnouncement: 'Your coffee match is ready.',
};
const DEFAULT_DESTINATION = '/collections/all';

class CoffeeQuiz extends HTMLElement {
  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
    if (!this.steps.length) return;

    this.markers = Array.from(this.querySelectorAll('[data-quiz-marker]'));
    this.status = this.querySelector('[data-quiz-status]');
    this.result = this.querySelector('[data-quiz-result]');
    this.resultHeading = this.querySelector('[data-quiz-result-heading]');
    this.resultAnswers = this.querySelector('[data-quiz-result-answers]');
    this.resultCta = this.querySelector('[data-quiz-result-cta]');
    this.controls = this.querySelector('[data-quiz-controls]');
    this.backButton = this.querySelector('[data-quiz-back]');
    this.restartButton = this.querySelector('[data-quiz-restart]');

    this.strings = this.readStrings();
    this.defaultDestination = this.dataset.defaultDestination || DEFAULT_DESTINATION;
    this.selections = [];
    this.index = 0;
    this.finished = false;

    this.onSubmit = this.handleSubmit.bind(this);
    this.onClick = this.handleClick.bind(this);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('click', this.onClick);

    this.show(0, { announce: false });
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.onSubmit);
    this.removeEventListener('click', this.onClick);
  }

  /* Strings come across as JSON rather than data-attributes so the Liquid side
     stays the single place English lives. Every read is optional-chained with
     a fallback: a merchant mid-edit can leave the block empty for a beat. */
  readStrings() {
    const node = this.querySelector('[data-quiz-strings]');
    let data = null;
    try {
      data = node ? JSON.parse(node.textContent) : null;
    } catch (error) {
      data = null;
    }
    return {
      stepAnnouncement: data?.stepAnnouncement || DEFAULT_STRINGS.stepAnnouncement,
      resultAnnouncement: data?.resultAnnouncement || DEFAULT_STRINGS.resultAnnouncement,
    };
  }

  handleSubmit(event) {
    const form = event.target.closest('[data-quiz-answer]');
    if (!form) return;
    event.preventDefault();

    this.selections[this.index] = {
      label: form.dataset.answerLabel || '',
      destination: form.dataset.destination || '',
    };

    const heading = this.steps[this.index].querySelector('[data-quiz-heading]');
    this.track('quiz_answer', {
      question_index: this.index + 1,
      question: heading ? heading.textContent.trim() : '',
      answer: this.selections[this.index].label,
      step_count: this.steps.length,
    });
    // Answering again after going back invalidates everything downstream.
    this.selections.length = this.index + 1;

    if (this.index + 1 < this.steps.length) {
      this.show(this.index + 1);
    } else {
      this.finish();
    }
  }

  handleClick(event) {
    if (event.target.closest('[data-quiz-back]')) {
      event.preventDefault();
      if (this.finished) this.show(this.steps.length - 1);
      else if (this.index > 0) this.show(this.index - 1);
      return;
    }
    if (event.target.closest('[data-quiz-restart]')) {
      event.preventDefault();
      this.selections = [];
      this.show(0);
    }
  }

  show(index, { announce = true } = {}) {
    this.index = index;
    this.finished = false;

    this.steps.forEach((step, i) => step.classList.toggle('is-active', i === index));
    this.markers.forEach((marker, i) => {
      marker.classList.toggle('is-active', i === index);
      marker.classList.toggle('is-done', i < index);
      if (i === index) marker.setAttribute('aria-current', 'step');
      else marker.removeAttribute('aria-current');
    });

    if (this.result) this.result.hidden = true;
    if (this.controls) this.controls.hidden = false;
    if (this.backButton) this.backButton.hidden = index === 0;
    if (this.restartButton) this.restartButton.hidden = index === 0;

    const heading = this.steps[index].querySelector('[data-quiz-heading]');
    if (!announce) return;

    if (heading) heading.focus();
    this.announce(
      this.strings.stepAnnouncement
        .replace('[current]', String(index + 1))
        .replace('[total]', String(this.steps.length))
        .replace('[question]', heading ? heading.textContent.trim() : '')
    );
  }

  finish() {
    const destination = this.buildDestination();
    this.finished = true;

    this.steps.forEach((step) => step.classList.remove('is-active'));
    this.markers.forEach((marker) => {
      marker.classList.add('is-done');
      marker.classList.remove('is-active');
      marker.removeAttribute('aria-current');
    });

    if (this.resultAnswers) {
      this.resultAnswers.replaceChildren(
        ...this.selections
          .filter((selection) => selection && selection.label)
          .map((selection) => {
            const item = document.createElement('li');
            item.className = 'quiz__chip chip';
            item.textContent = selection.label;
            return item;
          })
      );
    }
    if (this.resultCta) this.resultCta.href = destination;
    // The filters route to whatever the metafields say today, and metafields
    // drift: the known case was a roast filter on products that carry no
    // roast, landing people on "0 products". Rather than unhooking the quiz
    // from real inventory (a hand-picked result list goes stale the same
    // way, one shelf up), the destination is checked and quietly widened
    // when it would come back empty. The CTA is set optimistically first so
    // nothing waits on the network; the check usually beats the click.
    this.ensureDestinationHasProducts(destination);
    if (this.result) this.result.hidden = false;
    // The email capture rides with the result, carrying the answers as tags
    // so a signup arrives in the admin as "coffee-quiz" plus what they chose.
    // Commas separate Shopify tags, so they're stripped from the labels.
    const signup = this.querySelector('[data-quiz-signup]');
    if (signup) {
      signup.hidden = false;
      const tagsInput = signup.querySelector('[data-quiz-signup-tags]');
      if (tagsInput) {
        const answerTags = this.selections
          .filter((selection) => selection && selection.label)
          .map((selection) => `quiz:${selection.label.replace(/,/g, ' ').trim()}`);
        tagsInput.value = ['coffee-quiz', ...answerTags].join(', ').slice(0, 255);
      }
    }
    if (this.controls) this.controls.hidden = false;
    if (this.backButton) this.backButton.hidden = false;
    if (this.restartButton) this.restartButton.hidden = false;

    if (this.resultHeading) this.resultHeading.focus();
    this.announce(this.strings.resultAnnouncement);

    publish(QUIZ_COMPLETED, {
      destination,
      answers: this.selections.map((selection) => (selection ? selection.label : '')),
    });

    this.track('quiz_complete', {
      answers: this.selections.map((selection) => (selection ? selection.label : '')).join(' / '),
      destination,
      step_count: this.steps.length,
    });
  }

  /* Jack wants to know which answers people actually click. The sinks and the
     error swallowing live in theme-analytics.js, which the lead forms share. */
  track(name, payload) {
    if (typeof window.offshoreTrack === 'function') window.offshoreTrack(name, payload);
  }

  announce(message) {
    if (this.status) this.status.textContent = message;
  }

  /* Merge the chosen destinations into one URL. Relative hrefs are resolved
     against the current origin purely so URLSearchParams can do the parsing —
     the string handed back is always same-origin and path-relative. */
  /* Fetches the result page and counts its product cards. Empty, and the
     CTA falls back: first the same collection with the filters stripped,
     then the section's default. Product URLs are trusted as-is (a product
     page always contains its product), and any network failure leaves the
     original link alone — a fallback that needs the network to fail open. */
  async ensureDestinationHasProducts(destination) {
    if (!this.resultCta || destination.includes('/products/')) return;

    const hasProducts = async (href) => {
      const response = await fetch(href, { headers: { Accept: 'text/html' } });
      if (!response.ok) return true;
      const html = await response.text();
      return html.includes('class="card-product"');
    };

    try {
      if (await hasProducts(destination)) return;
      const unfiltered = destination.split('?')[0];
      const fallback =
        unfiltered !== destination && (await hasProducts(unfiltered))
          ? unfiltered
          : this.defaultDestination;
      // Only downgrade if the visitor is still looking at this result.
      if (this.finished && this.resultCta.getAttribute('href') === destination) {
        this.resultCta.href = fallback;
        this.track('quiz_result_fallback', { requested: destination, served: fallback });
      }
    } catch (error) {
      /* fail open: the original destination stays */
    }
  }

  buildDestination() {
    const chosen = this.selections
      .map((selection) => (selection ? selection.destination : ''))
      .filter(Boolean);

    const product = chosen.find((href) => href.includes('/products/'));
    if (product) return product;

    const base = new URL(this.defaultDestination, window.location.origin);
    const params = new URLSearchParams(base.search);
    let path = '';

    chosen.forEach((href) => {
      let url;
      try {
        url = new URL(href, window.location.origin);
      } catch (error) {
        return;
      }
      if (!path && url.pathname !== '/') path = url.pathname;
      url.searchParams.forEach((value, key) => {
        const duplicate = Array.from(params.entries()).some(
          ([existingKey, existingValue]) => existingKey === key && existingValue === value
        );
        if (!duplicate) params.append(key, value);
      });
    });

    const query = params.toString();
    return `${path || base.pathname}${query ? `?${query}` : ''}`;
  }
}

if (!customElements.get('coffee-quiz')) customElements.define('coffee-quiz', CoffeeQuiz);
